/**
 * Servicio de autenticación para widgets Vue.js
 * Este servicio maneja la validación de tokens de autenticación con el backend Django
 */

class WidgetAuthService {
  constructor() {
    this.token = null;
    this.apiToken = null;
    this.userInfo = null;
    this.backendUrl = this.getBackendUrl();
    this.isAuthenticated = false;
    this.authChecked = false;
  }

  /**
   * Obtiene la URL del backend desde las variables de entorno
   */
  getBackendUrl() {
    // Prioridad: backend_url en la URL (pasado desde Django con el host real del usuario)
    const urlParams = new URLSearchParams(window.location.search);
    const backendUrl = urlParams.get('backend_url');
    if (backendUrl) {
      return backendUrl;
    }
    if (import.meta.env.VITE_DJANGO_URL) {
      return import.meta.env.VITE_DJANGO_URL;
    }
    return 'http://localhost:8000';
  }

  /**
   * Inicializa la autenticación extrayendo el token de la URL
   */
  async init() {
    try {
      console.log('🔍 Iniciando autenticación de widget...');
      
      // Extraer token de los parámetros de la URL
      const urlParams = new URLSearchParams(window.location.search);
      this.token = urlParams.get('auth_token');
      

      if (!this.token) {
        console.warn('❌ No se encontró token de autenticación en la URL');
        this.redirectToLogin();
        return false;
      }

      console.log('✅ Token encontrado, procediendo a validar...');
      
      // Validar el token con el backend
      const isValid = await this.validateToken();
      
      if (!isValid) {
        console.error('❌ Token de autenticación inválido');
        this.redirectToLogin();
        return false;
      }

      this.isAuthenticated = true;
      this.authChecked = true;
      
      console.log('✅ Autenticación exitosa para usuario:', this.userInfo?.username);
      return true;

    } catch (error) {
      console.error('💥 Error durante la inicialización de autenticación:', error);
      this.redirectToLogin();
      return false;
    }
  }

  /**
   * Valida el token con el backend Django
   */
  async validateToken() {
    if (!this.token) {
      console.error('❌ No hay token para validar');
      return false;
    }

    console.log('🔄 Validando token con backend...');
    console.log('Backend URL:', this.backendUrl);

    try {
      const response = await fetch(`${this.backendUrl}/api/widget-auth/validate/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Incluir cookies de sesión
        body: JSON.stringify({
          token: this.token
        })
      });

      console.log('📡 Respuesta del servidor:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        console.error('❌ Error HTTP validando token:', response.status, response.statusText);
        
        // Intentar leer el cuerpo de la respuesta para más detalles
        try {
          await response.text();
          console.error('La validación de autenticación ha fallado.');
        } catch (e) {
          console.error('No se pudo leer el cuerpo del error');
        }
        
        return false;
      }

      const data = await response.json();

      
      if (!data.valid) {
        console.error('❌ Token inválido según servidor:', data.error);
        return false;
      }

      // Guardar información del usuario y DRF token para llamadas a la API
      this.userInfo = data.user;
      this.apiToken = data.api_token || null;
      console.log('✅ Token válido, información del usuario:', this.userInfo);
      return true;

    } catch (error) {
      console.error('💥 Error de red validando token:', error);
      console.error('Stack trace:', error.stack);
      return false;
    }
  }

  /**
   * Redirige al login del backend Django
   */
  redirectToLogin() {
    const loginUrl = `${this.backendUrl}/accounts/login/`;
    console.log('Redirigiendo al login:', loginUrl);
    
    // Usar replace para evitar que el usuario pueda volver atrás
    window.location.replace(loginUrl);
  }

  /**
   * Obtiene la información del usuario autenticado
   */
  getUserInfo() {
    return this.userInfo;
  }

  /**
   * Verifica si el usuario está autenticado
   */
  isUserAuthenticated() {
    return this.isAuthenticated;
  }

  /**
   * Limpia la información de autenticación
   */
  logout() {
    this.token = null;
    this.apiToken = null;
    this.userInfo = null;
    this.isAuthenticated = false;
    this.authChecked = false;
  }

  /**
   * Middleware para hacer peticiones autenticadas
   */
  async authenticatedFetch(url, options = {}) {
    if (!this.isAuthenticated || !this.apiToken) {
      throw new Error('Usuario no autenticado');
    }

    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
        'Authorization': `Token ${this.apiToken}`
      },
      credentials: 'include'
    };

    return fetch(url, { ...options, ...defaultOptions });
  }

  /**
   * Refresca el token de autenticación (si es necesario)
   * En esta implementación básica, no hacemos refresh automático
   */
  async refreshToken() {
    // Implementar si se necesita refresh automático de tokens
    console.log('Refresh de token no implementado en esta versión');
    return false;
  }
}

// Crear instancia única del servicio
const widgetAuthService = new WidgetAuthService();

// Plugin para Vue.js
const WidgetAuthPlugin = {
  install(app) {
    // Hacer el servicio disponible globalmente en la app Vue
    app.config.globalProperties.$auth = widgetAuthService;
    app.provide('widgetAuth', widgetAuthService);
  }
};

export { widgetAuthService, WidgetAuthPlugin };
export default widgetAuthService;
